import { lazy } from "react";

const RELOAD_FLAG = "photofilm:chunk-reload";

/**
 * A dynamic import can fail persistently after a deploy: stale chunk hash, or
 * an HTML response cached at a `.js` URL by the CDN. `<Suspense>` does not
 * catch those rejections — React unmounts the tree and the page goes blank.
 *
 * So we replay the import after repriming the HTTP cache entry, then fall back
 * to a single full reload.
 */

function hasAlreadyReloaded(): boolean {
  try {
    return sessionStorage.getItem(RELOAD_FLAG) !== null;
  } catch {
    // Storage unavailable: treat the reload as already attempted rather than
    // risk an infinite loop.
    return true;
  }
}

function markReloaded(): void {
  try {
    sessionStorage.setItem(RELOAD_FLAG, "1");
  } catch {
    /* storage unavailable */
  }
}

function clearReloadMark(): void {
  try {
    sessionStorage.removeItem(RELOAD_FLAG);
  } catch {
    /* storage unavailable */
  }
}

function extractModuleUrl(error: unknown): string | null {
  const message = error instanceof Error ? error.message : String(error);
  const match = message.match(/https?:\/\/[^\s"')]+\.(?:js|mjs|css)/);

  return match ? match[0] : null;
}

export async function importWithRetry<T>(factory: () => Promise<T>): Promise<T> {
  try {
    const module = await factory();
    clearReloadMark();

    return module;
  } catch (error) {
    const moduleUrl = extractModuleUrl(error);

    // `cache: "reload"` forces a network round-trip and rewrites the HTTP cache
    // entry, including one that was previously served as `immutable`.
    if (moduleUrl) {
      try {
        const response = await fetch(moduleUrl, { cache: "reload" });
        const contentType = response.headers.get("content-type") ?? "";

        if (response.ok && !contentType.includes("text/html")) {
          const module = await factory();
          clearReloadMark();

          return module;
        }
      } catch {
        /* the network had nothing better to offer, fall through to reload */
      }
    }

    // `index.html` is served with `max-age=0`, so a full reload always brings
    // back the chunk hashes of the current deployment.
    if (!hasAlreadyReloaded()) {
      markReloaded();
      window.location.reload();

      // Resolve nothing while the reload kicks in, to avoid flashing an error
      // that is about to disappear.
      return new Promise<T>(() => {});
    }

    throw error;
  }
}

type LazyComponentFactory = Parameters<typeof lazy>[0];

export function lazyWithRetry(factory: LazyComponentFactory) {
  return lazy(() => importWithRetry(factory));
}
